import {
  endOfWeek,
  format,
  parseISO,
  startOfDay,
  startOfWeek,
} from "date-fns";
import type { LamShiftRollsRow } from "../api/lamLine";
import type { LamGrouping } from "../components/common/GroupSelector";

/**
 * Per-shift roll counts, summed into calendar buckets.
 *
 * The PLC counts rolls per shift, so shifts are what the API returns; the
 * overview shows days or weeks. Both roll charts bucket here rather than each
 * doing its own: they sit one above the other, and a shift landing in a
 * different bucket on each would put the same rolls at two different x
 * positions — which is the thing AxisAlign.test.tsx exists to prevent.
 */
const ms = (iso: string) => {
  const d = parseISO(iso);
  return Number.isNaN(d.getTime()) ? Number.NaN : d.getTime();
};

export type RollBucket = {
  /** Bucket start as epoch ms. Unique, and sorts into calendar order. */
  key: number;
  at: Date;
  /** "Aug 9" for a shift or day; "Aug 9 – Aug 15" for a week. */
  label: string;
  /** "7 shifts" for a sum; the shift's own name when grouping by shift. */
  sublabel: string;
  shifts: number;
  a: number;
  b: number;
  f: number;
  total: number;
  /** The bucket has not finished, so it is not comparable with the rest. */
  is_partial: boolean;
};

export const bucketShiftRolls = (
  rows: LamShiftRollsRow[],
  group: LamGrouping
): RollBucket[] => {
  // "shift" is the grain the data arrives at, so each row is its own bucket and
  // keeps the shift's own name; the others sum rows into a calendar boundary.
  if (group === "shift") {
    return rows
      .slice()
      .sort((x, y) => ms(x.shift_start) - ms(y.shift_start))
      .map((r) => {
        const at = parseISO(r.shift_start);
        return {
          key: at.getTime(),
          at,
          label: format(at, "MMM d"),
          sublabel: r.shift_name,
          shifts: 1,
          a: r.a_rolls,
          b: r.b_rolls,
          f: r.f_rolls,
          total: r.total,
          is_partial: r.is_partial,
        };
      })
      .filter((b) => !Number.isNaN(b.key));
  }

  const startOf = group === "week" ? startOfWeek : startOfDay;
  const buckets = new Map<
    number,
    { at: Date; a: number; b: number; f: number; shifts: number; partial: boolean }
  >();

  for (const r of rows) {
    let at: Date;
    try {
      at = startOf(parseISO(r.shift_start));
      if (Number.isNaN(at.getTime())) continue;
    } catch {
      continue;
    }
    const key = at.getTime();
    const acc =
      buckets.get(key) ?? { at, a: 0, b: 0, f: 0, shifts: 0, partial: false };
    acc.a += r.a_rolls;
    acc.b += r.b_rolls;
    acc.f += r.f_rolls;
    acc.shifts += 1;
    acc.partial = acc.partial || r.is_partial;
    buckets.set(key, acc);
  }

  // A bucket is partial when it holds the shift still running, or when it is
  // the current day/week — either way it has not finished.
  const currentKey = startOf(new Date()).getTime();
  return Array.from(buckets.entries())
    .sort(([x], [y]) => x - y)
    .map(([key, v]) => ({
      key,
      at: v.at,
      // A week is named by the days it covers. Its start date alone reads as a
      // single day, which is the one thing this point is not.
      label:
        group === "week"
          ? `${format(v.at, "MMM d")} – ${format(endOfWeek(v.at), "MMM d")}`
          : format(v.at, "MMM d"),
      sublabel: `${v.shifts} shift${v.shifts === 1 ? "" : "s"}`,
      shifts: v.shifts,
      a: v.a,
      b: v.b,
      f: v.f,
      total: v.a + v.b + v.f,
      is_partial: v.partial || key === currentKey,
    }));
};

/** One line's buckets at its current grouping, as reported to the overview. */
export type RollBucketsReport = { group: LamGrouping; buckets: RollBucket[] };

